import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import RotatePlayLogo from '../components/RotatePlayLogo';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string;

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: { sitekey: string; callback: (token: string) => void; 'expired-callback'?: () => void }) => string;
      reset: (widgetId: string) => void;
    };
  }
}

export default function Signup() {
  const { signup } = useAuth();
  const navigate   = useNavigate();

  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [confirm,      setConfirm]      = useState('');
  const [error,        setError]        = useState('');
  const [loading,      setLoading]      = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  const turnstileRef    = useRef<HTMLDivElement>(null);
  const turnstileWidget = useRef<string | null>(null);

  // Render the Turnstile widget once its script has loaded
  useEffect(() => {
    let cancelled = false;
    const tryRender = () => {
      if (cancelled || !turnstileRef.current || turnstileWidget.current) return;
      if (!window.turnstile) { setTimeout(tryRender, 100); return; }
      turnstileWidget.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => setCaptchaToken(token),
        'expired-callback': () => setCaptchaToken(''),
      });
    };
    tryRender();
    return () => { cancelled = true; };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 8)  { setError('Password must be at least 8 characters'); return; }
    if (!captchaToken)        { setError('Please complete the captcha'); return; }
    setLoading(true);
    try {
      await signup(email.trim().toLowerCase(), password, captchaToken);
      navigate(`/confirm-email?email=${encodeURIComponent(email.trim().toLowerCase())}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Signup failed');
      // Turnstile tokens are single-use — reset so the user can retry
      if (turnstileWidget.current) window.turnstile?.reset(turnstileWidget.current);
      setCaptchaToken('');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <RotatePlayLogo />
        </div>

        <div className="card">
          <div className="card-title">Create Organizer Account</div>

          {error && <div className="alert alert-red">{error}</div>}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
                required
                style={{ marginTop: 4 }}
              />
            </div>
            <div>
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                style={{ marginTop: 4 }}
              />
              <span className="hint">Min 8 characters, include uppercase and a number.</span>
            </div>
            <div>
              <label>Confirm Password</label>
              <input
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
                style={{ marginTop: 4 }}
              />
            </div>

            <div ref={turnstileRef} />

            <button className="btn btn-primary" type="submit" disabled={loading || !captchaToken}>
              {loading ? 'Creating account…' : 'Create Account'}
            </button>
          </form>

          <div style={{ marginTop: 16, fontSize: '.875rem' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: 'var(--blue)' }}>Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
